import type { MessageItem } from '../types';
import type { ThreadMessageLike } from '@assistant-ui/react';

// Alias for JSON.parse output type — ReadonlyParsedArgs requires recursive value constraints
// that can't be expressed without importing from assistant-stream/utils directly.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ParsedArgs = Record<string, any>;

/**
 * Converts an array of MessageItem records (from DynamoDB) into ThreadMessageLike[]
 * compatible with assistant-ui's useExternalMessageConverter.
 *
 * Rules applied:
 * - D-35: Skip system-role messages
 * - D-36: Skip items with empty content and no tool data
 * - D-31: Group consecutive same-role items into one ThreadMessageLike
 * - D-32: tool_call → tool-call part, tool_result → tool-result part
 * - D-34: text-partial treated as regular text
 * - D-38: createdAt from first item in group
 * - D-39: id from first item in group
 */
export function convertMessages(
  items: MessageItem[],
): (ThreadMessageLike & { externalId?: string })[] {
  const result: (ThreadMessageLike & { externalId?: string })[] = [];
  let i = 0;

  while (i < items.length) {
    const current = items[i];

    // D-35: Skip system messages
    if (current.role === 'system') {
      i++;
      continue;
    }

    // D-36: Skip empty items with no tool data
    if (!current.content && !current.toolCallId && !current.toolName) {
      i++;
      continue;
    }

    // D-31: Collect consecutive items with the same role into a group
    const group: MessageItem[] = [current];
    let j = i + 1;
    while (j < items.length) {
      const next = items[j];
      // Skip system messages within lookahead
      if (next.role === 'system') {
        break;
      }
      if (next.role !== current.role) {
        break;
      }
      // D-36: Skip empty items silently
      if (!next.content && !next.toolCallId && !next.toolName) {
        j++;
        continue;
      }
      group.push(next);
      j++;
    }
    i = j;

    // Build ThreadMessageLike from the group
    const firstItem = group[0];
    const role = current.role;

    if (role === 'user') {
      // User: join all text/text-partial content into one text part
      const joinedText = group.map((item) => item.content).join('');

      result.push({
        id: firstItem.id,
        externalId: firstItem.externalId,
        role: 'user',
        content: [{ type: 'text' as const, text: joinedText }],
        createdAt: new Date(firstItem.createdAt),
      });
    } else {
      // Assistant: convert each item to the appropriate part type
      const parts: (
        | { type: 'text'; text: string }
        | {
            type: 'tool-call';
            toolCallId: string;
            toolName: string;
            args: ParsedArgs;
            argsText?: string;
            result?: string;
            isError?: boolean;
          }
      )[] = [];

      for (const item of group) {
        if (item.contentType === 'text' || item.contentType === 'text-partial') {
          // D-34: text-partial treated as regular text
          parts.push({ type: 'text', text: item.content });
        } else if (item.contentType === 'tool_call' && item.toolCallId && item.toolName) {
          // D-32: tool_call → tool-call part
          let parsedArgs = {};
          try {
            parsedArgs = JSON.parse(item.content || '{}');
          } catch {
            // keep empty
          }
          parts.push({
            type: 'tool-call' as const,
            toolCallId: item.toolCallId,
            toolName: item.toolName,
            args: parsedArgs as ParsedArgs,
            argsText: item.content || '',
          });
        } else if (item.contentType === 'tool_result' && item.toolCallId) {
          // D-32: tool_result → merge result into existing tool-call part
          // assistant-ui uses toolCallId as key, so we must not create a duplicate.
          const existingCall = parts.find(
            (p) => p.type === 'tool-call' && 'toolCallId' in p && p.toolCallId === item.toolCallId,
          );
          if (existingCall && 'toolCallId' in existingCall) {
            // Merge result into the existing tool-call part
            (existingCall as Record<string, unknown>).result = item.content;
            (existingCall as Record<string, unknown>).isError = false;
          } else {
            // No matching tool_call found — create standalone part
            parts.push({
              type: 'tool-call',
              toolCallId: item.toolCallId,
              toolName: item.toolName ?? '',
              args: {} as ParsedArgs,
              argsText: '',
              result: item.content,
              isError: false,
            });
          }
        }
      }

      result.push({
        id: firstItem.id,
        role: 'assistant',
        content: parts,
        createdAt: new Date(firstItem.createdAt),
      });
    }
  }

  return result;
}
